import CurrencyRate from 'components/CurrencyRate';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import { ActionType, CustomActionType } from 'further-types/lp/action';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import { CalendarDay } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '../FormWrapper';
import { useParams } from 'react-router-dom';
import { compact } from 'lodash';

type Props = {
  amountIncVatTcy: number;
  amountExVatTcy: number;
  currencyCode: CurrencyCode;
  vehicle: {
    _id: string;
    vehicleName: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  subtitle?: string;
  periodStartDate: CalendarDay;
  periodIndex: number;
};

const ReviewAccountTransactions: React.FC<Props> = ({
  amountIncVatTcy,
  amountExVatTcy,
  currencyCode,
  vehicle,
  subtitle,
  periodStartDate,
  periodIndex,
}: Props) => {
  const { setValue, watch, getValues } = useFormContext<FormValues>();
  const values = getValues();
  const { id } = useParams<{ id?: string }>();

  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId: vehicle._id,
  });

  const selectedTypeOption =
    values.category === 'income'
      ? typeOptions.incomeTypeOptions.find(
          (opt) => opt.value === values.incomeOrExpenseType,
        )
      : typeOptions.expenseTypeOptions.find(
          (opt) => opt.value === values.incomeOrExpenseType,
        );

  const isCustomActionType =
    selectedTypeOption?.customActionTypeId !== undefined;

  const actionType = isCustomActionType
    ? CustomActionType.CustomAction
    : (values.incomeOrExpenseType as ActionType);

  const [actionTransactions] =
    api.incomeAndExpenses.getTransactions.useSuspenseQuery({
      vehicleId: vehicle._id,
      incomeOrExpenseGroupId: id,
      actionType,
      customActionTypeId: isCustomActionType
        ? selectedTypeOption?.customActionTypeId
        : undefined,
      incomeOrExpenseState: values.incomeOrExpenseState,
      category: values.category,
      amountIncVatTcy,
      amountExVatTcy,
      currency: values.currencyCode,
      transactions:
        watch('apportionmentPeriods')?.[periodIndex]?.transactions ?? [],
    });

  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: values.vehicleId,
    currency: values.currencyCode,
    date: periodStartDate,
    defaultCurrencyRate: values.apportionmentPeriods[0]?.currencyRate ?? null,
    onRateChange: (rate) => {
      setValue(
        `apportionmentPeriods.${periodIndex}.currencyRate`,
        rate ?? undefined,
      );
    },
  });

  const { transactionTypeOptions } = useTransactionTypeSelectorOptions();

  const handleChangeTransactions = (transactions: Array<Transaction>) => {
    setValue(`apportionmentPeriods.${periodIndex}.transactions`, transactions);
  };

  return (
    <Column spacing="xs">
      <TransactionAllocator
        transactionTypeOptions={transactionTypeOptions}
        transactions={compact(actionTransactions ?? [])}
        onChange={(transactions) => handleChangeTransactions(transactions)}
        transactionCurrency={values.currencyCode}
        fxRate={fxRate}
        vehicle={vehicle}
        subtitle={subtitle}
        transactionsInFuture={periodStartDate.isInFuture()}
        currencyRateComponent={
          <CurrencyRate
            currency={currencyCode}
            date={periodStartDate}
            vehicle={vehicle}
            fxRate={fxRate}
            onRateChange={changeRate}
            isFetchingRate={isFetchingRate}
          />
        }
      />
    </Column>
  );
};

export default ReviewAccountTransactions;
